<template>
  <main class="container">
    <div class="row justify-content-center">
      <div class="col-lg-10">
        <header>
          <div class="section-title"></div>
          <div class="row">
            <div class="col-md-2">
              <img src="../assets/housing-matters-logo.png" alt="Housing Matters" />
            </div>
            <div class="col-md-9">
              <h1>Housing in Toronto is expensive because there's not enough of it.</h1>

              <p>Housing Matters is a coalition of Torontonians who advocate for increased housing supply through land use rule reform. If we want more people to have housing in Toronto, we're going to need to build more housing.</p>
            </div>
          </div>
        </header>
        <div class="section-title extra-margin"></div>
        <div class="sections">
          <section v-for="(section, index) in sections" v-bind:key="index" :class="{ open: openSections.indexOf(index + 1) > -1 }">
            <div class="top row justify-content-center" v-on:click="toggleSection(index + 1)">
              <div class="col-md-9 offset-md-1">
                <h2>
                  <span class="number">{{ padNumber(index + 1) }}</span>
                  {{ section.title }}
                </h2>
                <span class="toggle"></span>
              </div>
            </div>
            <div class="bottom row justify-content-center">
              <div class="col-md-9 offset-md-1" v-html="section.content"></div>
            </div>
          </section>
        </div>
        <div class="section-title"></div>
        <p class="download-container"><a href="/hm-yellowbelt-sep-30-2019.pdf" class="btn btn-pink" target="_blank" rel="noopener noreferrer">Download Report</a></p>
        <div class="section-title"></div>
        <footer class="row justify-content-between">
          <div class="col-md-5">
            <p>Join the discussion</p>
            <ul class="social">
              <li>
                <a href="https://twitter.com/torontohousing_" target="_blank" rel="noopener noreferrer">
                  <img src="../assets/twitter.svg" alt="Twitter" />@torontohousing_
                </a>
              </li>
              <li>
                <a href="mailto:hello@torontohousingmatters.com" target="_blank" rel="noopener noreferrer">
                  <img src="../assets/email.svg" alt="Email" />hello@torontohousingmatters.com
                </a>
              </li>
            </ul>
          </div>
          <div class="col-md-6">
            <p>Sign up to our newsletter to learn more about upcoming events, important developments, and more.</p>
            <form action="https://torontohousingmatters.us15.list-manage.com/subscribe/post?u=be389aee243cbde8cdf7c2c1d&amp;id=5713181ff9" method="post" id="mc-embedded-subscribe-form" name="mc-embedded-subscribe-form" class="form" target="_blank" novalidate>
              <div style="position: absolute; left: -5000px;" aria-hidden="true"><input type="text" name="b_be389aee243cbde8cdf7c2c1d_5713181ff9" tabindex="-1" value=""></div>
              <div class="form-group">
                <input type="email" value="" name="EMAIL" class="form-control" id="mce-EMAIL" placeholder="Your Email">
              </div>
              <div class="form-group">
                <input type="text" value="" name="MMERGE3" class="form-control" id="mce-MMERGE3" placeholder="Your Postal Code">
              </div>
              <div class="form-group">
                <button type="submit" name="subscribe" id="mc-embedded-subscribe" class="btn btn-pink">Sign Up</button>
              </div>
            </form>
          </div>
        </footer>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'Home',
  data: function () {
    return {
      activeSection: 1,
      openSections: [],
      sections: [
        {
          title: 'The Context',
          content: `<p>Over the last twenty years, Toronto has worked hard to earn itself a reputation as a global city. The result of many years of policy, advertising, and innovation, Toronto is now known as a welcoming hub of business, entertainment, and culture. Unsurprisingly, this has also resulted in a huge increase in the population growth of the city: over 400,000 people have moved to the city since 2001, and another 1 million have moved to the GTA.</p>
            <p>This tremendous growth has had its share of strain on the city. Most notably, it has become harder and harder to find an affordable place to live.</p>
            <p>See pages 13-20 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        },
        {
          title: 'The Crisis',
          content: `<p>The squeeze on the availability of housing has had major effects on housing prices and rents. Housing prices have grown by 145% since the year 2000. Rent has been increasing aggressively, too, with the average rent for a one-bedroom apartment now sitting at $2,300 per month. Incomes, however, have not been keeping up. As a result, Torontonians must borrow more, spend more, and allocate a larger portions of their incomes just to have a place to live in the city.</p>
            <p>By our estimate, the typical Torontonian now spends nearly 40% of their income on housing. As the government of Ontario defines housing affordability as being below 30% of income, Toronto is well past that threshold.</p>
            <p>From these facts, our only conclusion is that the city is simply not building enough homes. This is a simple story of supply and demand: there are more people who want to live in this city than there are homes being built to accommodate them. This is despite the fact that Toronto has been building new homes at a record pace.</p>
            <p>See pages 20-28 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        },
        {
          title: 'Land Use Rules',
          content: `<p>The fact that we aren’t building enough homes for a growing city explains the how of the major price increases to the city. But the real question is why? What is limiting the supply of new homes? Our answer: land use rules.</p>
            <p>Land use rules are how city officials determine the quantity, type, and size of housing. In Toronto, land use rules are a complicated web of provincial and municipal Policies, Plans, and By-laws.</p>
            <p>While most people are familiar with the Zoning By-law, few are aware that the Zoning By-law is actually an implementation of another City ordinance—the Official Plan.</p>
            <p>The Official Plan sets out the broad policy objectives of urban planning in the city. This includes things like where the city should focus on growing, what should be preserved, and more.</p>
            <p>See pages 29-34 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        },
        {
          title: 'The Yellowbelt',
          content: `<p>One chapter in particular on land use designations discusses “Neighbourhoods”. In these areas of the city, the Official Plan requires that all new housing must preserve and reinforce the existing “physical character” of the surrounding area. What this means in practice is that in nearly half the city, the only new type of construction allowed are low-density housing types, up to a fourplex. And out of that half, nearly two-thirds is zoned exclusively for detached housing. We call this area of constrained growth the Yellowbelt.</p>
            <img src="${require('../assets/yellowbelt-map.png')}" alt="Yellowbelt Map" />
            <p>Meanwhile, all the high-rise development in the city has been focused on just over 3% of the city!</p>
            <p>See pages 38-41 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        },
        {
          title: 'Is Toronto Filling Up?',
          content: `<p>Perhaps the most surprising fact of all about Toronto's population growth is that in much of the city, the population of many neighbourhoods has been declining! And these declines aren't recent either—huge swaths of land in major areas have lost hundreds of people per hectare since 1971. As a result, despite some local crowding, many of Toronto’s public service systems are operating under capacity: including the TTC, wastewater treatment, and even schools.</p>
            <p>Toronto has plenty of room for growth. By constraining the supply of new homes in the city as more people try to move in, these policies are directly responsible for the surge in housing prices.</p>
            <p>See pages 35-38 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        },
        {
          title: 'Missing Middle',
          content: `<p>Does this mean that the only way forward is to turn the entire city into a jungle of skyscrapers? Hardly.</p>
            <p>Currently, the two most predominant housing types in the city are detached homes and high-rise condos and apartments. There is a wide variety of housing types in the middle of this spectrum that are missing from our landscape: townhouses, triplexes, fourplexes, walk-up apartments, and the creative combinations in between.</p>
            <img src="${require('../assets/missing-middle.png')}" alt="The Missing Middle" />
            <p>What we are calling for is a loosening of the Yellowbelt to allow for more of these middle housing types.</p>
            <p>Since most of these developments don't have to be taller than three or four stories, they can be placed in those existing communities with declining population densities and underused public facilities, and their impact can be much less than alternative, high-density building types.</p>
            <p>See pages 42-46 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        },
        {
          title: 'More Supply of All Kinds Helps All Income Groups',
          content: `<p>Many critics of Toronto’s housing policy point out that new supply is mostly made up of expensive “luxury” homes and condos, and so this would do nothing to help housing affordability. They argue instead that we need the “right” supply of housing, namely more “affordable” housing options, meaning below market-rate.</p>
            <p>There is undoubtedly a need for purpose-built affordable housing. Still, it's important to note that by increasing the supply of market-rate housing, the price drops for all housing types.</p>
            <p>Luxury housing competes with moderate housing the same way that an iPhone competes with a Nokia, or filet mignon competes with chuck steak: precisely because they serve different segments of the market, they prevent spillovers from high income buyers being pushed to bid away lower-quality units from low-income buyers. By allowing the market to build as much luxury housing as it can sell, this frees up land and other existing (if slightly less luxurious) housing for lower-income groups.</p>
            <p>By limiting the supply of luxury homes through overly-restrictive land-use rules, the Yellowbelt effectively limits competition. This results in higher prices and fewer housing options for everyone, as high-income individuals will bid away housing that would otherwise have been available for someone else.</p>
            <p>See pages 50-51 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        },
        {
          title: 'Broader Issue',
          content: `<p>Why is this important? Not only would it help keep prices in Toronto low, but it would also help keep Toronto attractive as a global city by enabling more entrepreneurship, more innovation, a decrease in poverty, and even less environmental impact. Here's how.</p>
            <p>First, cities result in faster job creation. In the words of the well-traveled urban planner Alain Bertaud, cities are primarily labour markets. Cities, as superconnected economic areas, allow for the mobility required for many job seekers and employers to quickly find matches to fill positions.</p>
            <p>Second, cities are where innovation happens. Innovation comes from the sharing of ideas. This is because in cities, the vast confluence of people of different backgrounds enable ideas to have sex—by mixing different ideas together, you get the chance to produce all kinds of new ideas as an outcome.</p>
            <p>Third, cities decrease poverty. This point directly flows from the previous two. The more income people have, the less impoverished they are. The more jobs there are, the more experienced and connected locals can move up to find higher paying jobs, while inexperienced immigrants will want to move into the city.</p>
            <p>Finally, cities help preserve the environment and produce more and better food. As people and production become clustered in central locations, they free up arable land elsewhere to focus on growing food. By freeing up this land to be put to use for food production, we waste fewer resources producing food in infertile land, while simultaneously allowing ourselves to produce more and better quality food on less land.</p>
            <p>Does this mean building more homes is a panacea to all our social ills? Obviously not. There are many costs to city life, yet hundreds of thousands are choosing to make the Toronto their new home. The only question for policy makers, then, is whether to embrace these preferences, or to fight them.</p>
            <p>See pages 47-50 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        },
        {
          title: 'Proposal',
          content: `<p>Our proposal is basically two-fold.</p>
            <p>First, we want to unlock the Yellobwelt by allowing more dense housing types to be built. This means getting rid of the language in the Official Plan that require new homes to reinforce existing housing types, and allowing anything up to a four-storey walk-up apartment to be built anywhere in the Yellowbelt.</p>
            <p>Second, we want to shrink the Yellowbelt by re-designating and re-zoning more of it for “mixed-use”. This means more live-work buildings, such as condos with office or retail space, or simply walk-up apartments that have a small shop or office at street level.</p>
            <p>As a final proposal, we also suggest that the city implement a new monitoring framework for determining where other major bottlenecks are in the development application or re-zoning process.</p>
            <p>See pages 54-58 <a href="/hm-yellowbelt-sep-30-2019.pdf" target="_blank" rel="noopener noreferrer">of the report</a> to learn more.</p>`
        }
      ]
    }
  },
  methods: {
    padNumber: function (number) {
      if (number < 10) {
        return '0' + number
      }
    },
    selectSection: function (section) {
      this.activeSection = section
    },
    toggleSection: function (section) {
      let index = this.openSections.indexOf(section)
      if (index > -1) {
        this.openSections.splice(index, 1)
      }
      else {
        this.openSections.push(section)
      }
    }
  },
  components: {
  }
}
</script>

<style lang="scss" scoped>
// import vars
@import "../stylesheets/_variables";

div.section-title {
  font-family: $serifFont;
  font-style: italic;
  padding-bottom: 20px;
  border-bottom: 1px solid #F0F0F0;
  margin-bottom: 20px;
  line-height: 1em;

  &.extra-margin {
    margin-bottom: 60px;
  }
}

p {
  font-family: $serifFont;
  line-height: calc(28/18);
  margin: 0 0 1.5em;

  &:last-child {
    margin-bottom: 0;
  }
}

.btn.btn-pink {
  font-family: $sansSerifFont;
  background-color: $highlight;
  color: white;
  font-weight: 500;
  padding: 0.5rem 2.5rem;

  &:hover {
    background-color: darken($highlight, 10%);
  }
}

header {
  margin-top: 85px;
  margin-bottom: 60px;

  div.section-title {
    margin-bottom: 80px;
  }

  img {
    width: 82px;
    height: auto;

    @media all and (max-width: $mobileMax) {
      margin: 0 0 20px;
    }
  }

  h1 {
    color: $highlight;
    font-weight: 700;
    font-size: 30px;
    line-height: calc(36/30);
    margin-bottom: 20px;
  }
}

div.sections {
  margin-bottom: 50px;
}

section {
  border-bottom: 1px solid #F0F0F0;
  padding-top: 20px;

  &:last-child {
    border-bottom: none;
  }

  div.top {
    cursor: pointer;
    padding-bottom: 20px;
    position: relative;

    div[class^='col-'] {
      position: static;
    }

    &:hover {
      h2 {
        color: $highlight;
      }

      span.toggle {
        opacity: 1;
      }
    }

    h2 {
      color: #000;
      font-size: 25px;
      font-weight: 700;
      margin: 0;
      transition: color 250ms;

      @media all and (max-width: $mobileMax) {
        padding: 0 0 0 60px;
      }

      span.number {
        color: #000;
        position: absolute;
        left: 15px;
      }
    }

    span.toggle {
      position: absolute;
      right: 15px;
      top: 18px;
      display: block;
      width: 20px;
      height: 2px;
      background-color: $highlight;
      opacity: 0;
      transition: opacity 250ms;

      &:before {
        content: "";
        position: absolute;
        left: 9px;
        top: -9px;
        height: 20px;
        width: 2px;
        background-color: $highlight;
      }
    }
  }

  div.bottom {
    padding-bottom: 0px;
    max-height: 0px;
    transition: max-height 500ms, padding-bottom 250ms;
    overflow: hidden;
  }

  &.open {
    div.top {
      h2 {
        color: $highlight;
      }

      span.toggle {
        opacity: 1;

        &:before {
          opacity: 0 !important;
        }
      }
    }

    div.bottom {
      max-height: 1500px;
      padding-bottom: 60px;
    }
  }
}

p.download-container {
  margin: 3.5em 0 2.25em;
  text-align: center;
}

footer {
  margin-top: 55px;
  margin-bottom: 100px;

  ul.social {
    margin-top: 35px;
    margin-left: 10px;
    padding: 0;
    list-style: none;

    li {
      padding-bottom: 1em;

      a {
        font-size: 14px;
        font-family: $serifFont;
        color: #000;
        text-decoration: underline;
        transition: color 250ms;

        &:hover {
          color: $highlight;
        }

        img {
          padding: 0 20px 0 0;
        }
      }
    }
  }

  form.form {
    div.form-group {
      margin-bottom: 25px;

      &:last-child {
        text-align: right;
        margin-bottom: 0;
      }

      input.form-control {
        border-radius: 5px;
        border: 2px solid #EFEFEF;
        font-size: 14px;
        height: calc(3em + 0.75rem + 2px);
        padding: 0.375rem 1.5rem;

        &::placeholder {
          color: #CDCDCD;
        }
      }
    }
  }
}
</style>
<style lang="scss">
// import vars
@import "../stylesheets/_variables";

p {
  &:last-child {
    margin-bottom: 0;
  }
}

div.bottom {
  img {
    max-width: 100%;
    height: auto;
    margin: 0 0 1em;
  }

  a {
    color: $highlight;
    text-decoration: underline;
  }
}
</style>
